'use client'

import { useState, useEffect } from 'react'
import { usePWA } from '@/hooks/usePWA'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Download, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

export function InstallPrompt() {
  const { t } = useI18n()
  const { isInstallable, installApp } = usePWA()
  const [isDismissed, setIsDismissed] = useState(true)

  useEffect(() => {
    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed')
    if (!dismissed && isInstallable) {
      // Show prompt after a delay
      const timer = setTimeout(() => {
        setIsDismissed(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [isInstallable])

  const handleInstall = async () => {
    const installed = await installApp()
    if (installed) {
      setIsDismissed(true)
    }
  }

  const handleDismiss = () => {
    setIsDismissed(true)
    localStorage.setItem('pwa-install-dismissed', 'true')
  }

  if (isDismissed || !isInstallable) return null

  return (
    <div
      className={cn(
        'fixed bottom-20 left-4 right-4 z-50',
        'animate-in slide-in-from-bottom duration-300'
      )}
    >
      <Card className="shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Download className="h-5 w-5" />
            </div>

            <div className="flex-1">
              <h3 className="font-medium">{t('common.installAppTitle')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('common.installAppDesc')}
              </p>

              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleInstall}>
                  {t('common.install')}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleDismiss}>
                  {t('common.later')}
                </Button>
              </div>
            </div>

            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 shrink-0"
              onClick={handleDismiss}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
